import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useAuth } from '@/lib/auth';
import { useIsAdmin } from '@/hooks/useRole';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

function IconBase({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      {children}
    </svg>
  );
}

function ServicesIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007M3.75 12h.007M3.75 17.25h.007"
      />
    </IconBase>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827a7.723 7.723 0 0 1 0 .255l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827a6.932 6.932 0 0 1 0-.255l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </IconBase>
  );
}

function ChecklistIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </IconBase>
  );
}

function TeamIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 18.72A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A5.995 5.995 0 0 1 12 12.75a5.995 5.995 0 0 1 5.999 5.969ZM15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </IconBase>
  );
}

function AccountIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
      />
    </IconBase>
  );
}

function HelpIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.745.652-2.12.861-2.12 2.269v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008"
      />
    </IconBase>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
      />
    </IconBase>
  );
}

function NotificationsIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a3 3 0 1 1-5.714 0"
      />
    </IconBase>
  );
}

function ChangelogIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18V4.875c0-.621.504-1.125 1.125-1.125h11.25c.621 0 1.125.504 1.125 1.125V7.5Z"
      />
    </IconBase>
  );
}

function ScoutIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6"
      />
    </IconBase>
  );
}

function RunsIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </IconBase>
  );
}

function SuppressionIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
      />
    </IconBase>
  );
}

function LeadsIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
      />
    </IconBase>
  );
}

function SignOutIcon({ className }: { className?: string }) {
  return (
    <IconBase className={className}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
      />
    </IconBase>
  );
}

const SCOUT_GROUP: NavGroup = {
  label: 'Research Runs',
  items: [
    { to: '/scout/compose', label: 'Criteria Composer', icon: ScoutIcon },
    { to: '/scout/runs', label: 'Runs', icon: RunsIcon },
    { to: '/scout/export', label: 'Leads', icon: LeadsIcon },
    { to: '/scout/suppressions', label: 'Workspace Criteria', icon: SuppressionIcon },
  ],
};

function buildSettingsGroup(isAdmin: boolean): NavGroup {
  const items: NavItem[] = [
    { to: '/settings', label: 'Workspace', icon: SettingsIcon },
    { to: '/settings/account', label: 'Account', icon: AccountIcon },
    { to: '/settings/setup-checklist', label: 'Setup Checklist', icon: ChecklistIcon },
  ];
  if (isAdmin) {
    items.push({ to: '/settings/team', label: 'Team & Roles', icon: TeamIcon });
  }
  return { label: 'Settings', items };
}

function buildAdminGroup(isSuperadmin: boolean): NavGroup | null {
  if (!isSuperadmin) return null;
  return {
    label: 'Platform',
    items: [{ to: '/admin/workspaces', label: 'Workspaces', icon: ServicesIcon }],
  };
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const entitlements = useEntitlements();
  const isAdmin = useIsAdmin();
  const { user, org, logout } = useAuth();
  const isSuperadmin = user?.platformRole === 'superadmin';

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U';

  const navGroups: NavGroup[] = [];
  if (entitlements.scout) navGroups.push(SCOUT_GROUP);
  navGroups.push(buildSettingsGroup(isAdmin));
  const adminGroup = buildAdminGroup(isSuperadmin);
  if (adminGroup) navGroups.push(adminGroup);

  const [hasUnseenNotifications, setHasUnseenNotifications] = useState(() => {
    try {
      return !localStorage.getItem('11to1_lastSeenNotificationsAt');
    } catch {
      return false;
    }
  });
  const [hasUnseenChangelog, setHasUnseenChangelog] = useState(() => {
    try {
      return localStorage.getItem('11to1_lastSeenChangelogVersion') !== '0.1.0';
    } catch {
      return false;
    }
  });

  const handleNotificationsClick = useCallback(() => {
    setHasUnseenNotifications(false);
    try {
      localStorage.setItem('11to1_lastSeenNotificationsAt', new Date().toISOString());
    } catch {
      console.warn('[Sidebar] Failed to persist lastSeenNotificationsAt');
    }
    onClose();
  }, [onClose]);

  const handleChangelogClick = useCallback(() => {
    setHasUnseenChangelog(false);
    try {
      localStorage.setItem('11to1_lastSeenChangelogVersion', '0.1.0');
    } catch {
      console.warn('[Sidebar] Failed to persist lastSeenChangelogVersion');
    }
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  const handleFocusTrap = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== 'Tab' || !sidebarRef.current) return;
    const focusableElements = sidebarRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
    }
  }, []);

  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  const isMobileHidden = !isDesktop && !isOpen;
  const inertProps = isMobileHidden ? { inert: '' } : {};

  return (
    <aside
      id="mobile-sidebar"
      ref={sidebarRef}
      className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-surface border-r border-border
        transform transition-transform duration-subtle lg:translate-x-0
        flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
      aria-hidden={isMobileHidden}
      {...(inertProps as React.HTMLAttributes<HTMLElement>)}
      onKeyDown={isOpen ? handleFocusTrap : undefined}
    >
      <div className="flex h-14 items-center justify-between border-b border-border px-4 shrink-0">
        <Link to="/settings" className="flex items-center gap-2" onClick={onClose}>
          <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center">
            <span className="text-white font-bold text-sm">11</span>
          </div>
          <span className="font-semibold text-text">11to1</span>
        </Link>
        <button
          ref={closeButtonRef}
          type="button"
          className="lg:hidden p-1.5 rounded-md text-muted hover:text-text hover:bg-bg transition-colors duration-fast"
          onClick={onClose}
          aria-label="Close sidebar"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6" aria-label="Main navigation">
        {navGroups.map((group) => (
          <div key={group.label}>
            <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-muted">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const isActive =
                  item.to === '/settings'
                    ? location.pathname === '/settings' || location.pathname === '/settings/'
                    : location.pathname.startsWith(item.to);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onClose}
                      className={`
                        flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium
                        transition-colors duration-fast
                        ${isActive ? 'bg-secondary/10 text-secondary' : 'text-muted hover:text-text hover:bg-surface'}
                      `}
                    >
                      <item.icon className={`h-5 w-5 ${isActive ? 'text-secondary' : ''}`} />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-border px-3 py-3 space-y-0.5">
        <Link
          to="/help"
          onClick={onClose}
          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-fast ${
            location.pathname.startsWith('/help')
              ? 'bg-secondary/10 text-secondary'
              : 'text-muted hover:text-text hover:bg-surface'
          }`}
        >
          <HelpIcon
            className={`h-5 w-5 ${location.pathname.startsWith('/help') ? 'text-secondary' : ''}`}
          />
          Help
        </Link>

        <a
          href="https://11to1.com/blog"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted hover:text-text hover:bg-surface transition-colors duration-fast"
        >
          <BookOpenIcon className="h-5 w-5" />
          Knowledge Base
        </a>

        <button
          type="button"
          onClick={handleNotificationsClick}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted hover:text-text hover:bg-surface transition-colors duration-fast"
        >
          <span className="relative">
            <NotificationsIcon className="h-5 w-5" />
            {hasUnseenNotifications && (
              <span
                className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-warning"
                aria-label="Unseen notifications"
              />
            )}
          </span>
          Notifications
        </button>

        <button
          type="button"
          onClick={handleChangelogClick}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted hover:text-text hover:bg-surface transition-colors duration-fast"
        >
          <span className="relative">
            <ChangelogIcon className="h-5 w-5" />
            {hasUnseenChangelog && (
              <span
                className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary"
                aria-label="New changelog entries"
              />
            )}
          </span>
          Changelog
        </button>
      </div>

      <div className="shrink-0 p-4 border-t border-border">
        <div className="flex items-center gap-3">
          <Link
            to="/settings/account"
            onClick={onClose}
            className="flex flex-1 min-w-0 items-center gap-3 group"
          >
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary text-sm font-medium">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text truncate group-hover:text-primary transition-colors duration-fast">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-muted truncate">
                {user?.email || (org?.name ? org.name : '')}
              </p>
            </div>
          </Link>
          <button
            type="button"
            onClick={() => {
              onClose();
              logout();
            }}
            className="p-1.5 rounded-md text-muted hover:text-text hover:bg-bg transition-colors duration-fast"
            aria-label="Sign out"
            title="Sign out"
          >
            <SignOutIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
